import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api, { eur } from "@/lib/api";
import { eur as eur2 } from "@/lib/price";
import { useAuth } from "@/context/AuthContext";
import MagazzinoDetailDialog from "@/components/MagazzinoDetailDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, RotateCcw, Boxes, DownloadCloud, Pencil, Trash2, Sparkles, Info, ChevronRight, FileDown, ArrowUp, ArrowDown, Globe, Percent, ImageDown } from "lucide-react";
import { toast } from "sonner";

const eurN = (v) => (v == null ? "—" : eur2(v));
const isPanel = (t) => (t || "").toLowerCase().includes("modul");
const eurWp = (v, p) => {
  const base = eurN(v);
  if (!isPanel(p?.tipologia)) return base;
  const val = Number(v), wp = Number(p?.kwp || 0) * 1000;
  if (!val || val <= 0 || !wp || wp <= 0) return base;
  const w = (val / wp).toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return <>{base} <span className="text-[11px] font-normal text-muted-foreground">({w} €/Wp)</span></>;
};
const sortValue = (p) => {
  const prezzo = Number(p?.prezzo_min_installatore || 0);
  if (isPanel(p?.tipologia)) {
    const wp = Number(p?.kwp || 0) * 1000;
    const v = wp > 0 ? prezzo / wp : 0;
    return v > 0 ? v : Infinity;
  }
  return prezzo > 0 ? prezzo : Infinity;
};
const kwpN = (v) => (v ? Number(v).toLocaleString("it-IT", { maximumFractionDigits: 3 }) + " kWp" : "—");
const pctCell = (v) => {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  const neg = v < 0;
  return <span className={neg ? "text-red-500" : "text-emerald-600 dark:text-emerald-400"}>{v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span>;
};
const EMPTY = { codice: "", descrizione: "", tipologia: "", marca: "TORRI SOLARE", um: "Pz", kwp: 0, prezzo: 0, prezzo_target: 0, costo: 0, larghezza_m: 0, altezza_m: 0, lunghezza_m: 0, abilitato: false };

// Data listino gestionale (es. "2024-03-04") -> formato italiano.
const dateN = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Ordine di visualizzazione delle categorie (le altre in coda, alfabetiche).
const CAT_ORDER = ["Moduli Fotovoltaici", "Inverter", "Accumulo", "Fissaggio", "Quadri", "Quadri Custom", "Monitoraggio", "ELETTRONICA", "Accessori"];
const catRank = (t) => { const i = CAT_ORDER.indexOf(t); return i === -1 ? 999 : i; };

export default function Listino() {
  const { user } = useAuth();
  const canMag = ["admin", "back_office", "head_of_partner"].includes(user?.role) || user?.role_actual === "back_office";
  const [magDetail, setMagDetail] = useState(null);
  const [data, setData] = useState({ products: [], tipologie: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", tipologia: "all", abilitato: "all" });
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [refreshPrices, setRefreshPrices] = useState(false);
  const [pending, setPending] = useState(null);
  const [selCodici, setSelCodici] = useState(() => new Set());
  const [enriching, setEnriching] = useState(false);
  const [cachingImg, setCachingImg] = useState(false);
  const [edit, setEdit] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDist, setDownloadingDist] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [pdfCatOpen, setPdfCatOpen] = useState(false);
  const [pdfDest, setPdfDest] = useState("agenzia");
  const [pdfLang, setPdfLang] = useState("it");
  const [selCodes, setSelCodes] = useState([]);
  const [expCats, setExpCats] = useState([]);
  const [marginOpen, setMarginOpen] = useState(false);
  const [marginData, setMarginData] = useState(null);
  const [marginLoading, setMarginLoading] = useState(false);
  const [downloadingMarginPdf, setDownloadingMarginPdf] = useState(false);
  const toggleExpand = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openMargin = async () => {
    setMarginOpen(true);
    if (marginData) return;
    setMarginLoading(true);
    try {
      const r = await api.get("/listino/margine-magazzino");
      setMarginData(r.data);
    } catch { toast.error("Errore nel caricamento dell'analisi margini"); }
    finally { setMarginLoading(false); }
  };

  const downloadMarginPdf = async () => {
    setDownloadingMarginPdf(true);
    try {
      const r = await api.get("/listino/margine-magazzino/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `margini_costo_contabilita_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del PDF"); }
    finally { setDownloadingMarginPdf(false); }
  };

  const downloadPdf = async (codes) => {
    setPdfCatOpen(false); setDownloadingPdf(true);
    if (pdfLang !== "it") toast.info("Generazione e traduzione in corso, può richiedere qualche secondo…");
    try {
      const q = new URLSearchParams();
      q.set("destinatario", pdfDest);
      q.set("lang", pdfLang);
      if (f.search) q.set("search", f.search);
      if (codes && codes.length && codes.length < allCodes.length) q.set("codici", codes.join(","));
      q.set("abilitato", "1");
      const r = await api.get(`/listino/pdf?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `listino_torri_${pdfDest}_${pdfLang}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del PDF"); }
    finally { setDownloadingPdf(false); }
  };
  const downloadDistPdf = async (lang, codes) => {
    setLangOpen(false); setDownloadingDist(true);
    if (lang !== "it") toast.info("Generazione e traduzione in corso, può richiedere qualche secondo…");
    try {
      const q = new URLSearchParams();
      q.set("lang", lang);
      if (f.search) q.set("search", f.search);
      if (codes && codes.length && codes.length < allCodes.length) q.set("codici", codes.join(","));
      q.set("abilitato", "1");
      const r = await api.get(`/listino/distributore/pdf?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `listino_distributori_${lang}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del PDF distributore"); }
    finally { setDownloadingDist(false); }
  };
  const [saving, setSaving] = useState(false);
  const pollRef = useRef(null);

  // Alla comparsa dei nuovi prodotti, di default sono tutti selezionati.
  useEffect(() => {
    if (pending) setSelCodici(new Set(pending.map((p) => p.codice)));
  }, [pending]);
  const toggleSel = (codice) => setSelCodici((s) => { const n = new Set(s); n.has(codice) ? n.delete(codice) : n.add(codice); return n; });
  const allSelected = !!pending && pending.length > 0 && selCodici.size === pending.length;
  const toggleSelAll = () => setSelCodici(() => (allSelected ? new Set() : new Set((pending || []).map((p) => p.codice))));
  // Raggruppa i prodotti in attesa per tipologia (import più rapido).
  const pendingGroups = useMemo(() => {
    const m = {};
    (pending || []).forEach((p) => { const t = p.tipologia || "Senza tipologia"; (m[t] = m[t] || []).push(p); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [pending]);
  const toggleSelGroup = (codes, on) => setSelCodici((s) => { const n = new Set(s); codes.forEach((c) => (on ? n.add(c) : n.delete(c))); return n; });

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (f.search) q.set("search", f.search);
    if (f.tipologia !== "all") q.set("tipologia", f.tipologia);
    if (f.abilitato !== "all") q.set("abilitato", f.abilitato === "on" ? "1" : "0");
    api.get(`/listino?${q.toString()}`).then((r) => setData(r.data)).catch(() => toast.error("Errore nel caricamento")).finally(() => setLoading(false));
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const can = data.can_manage;
  const canCost = data.can_manage || data.can_view_costs;
  const isOwner = data.is_owner;

  const runImport = async () => {
    setImporting(true); setImportMsg("Avvio import…");
    try {
      await api.post("/listino/import-fier", { refresh_prices: refreshPrices });
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get("/listino/import-fier/status");
          setImportMsg(r.data.message || "In corso…");
          if (r.data.finished) {
            clearInterval(pollRef.current); pollRef.current = null; setImporting(false);
            if (r.data.error) toast.error("Import fallito: " + r.data.error);
            else {
              toast.success(r.data.message || "Import completato"); setImportOpen(false); load();
              if (r.data.new > 0) { try { const pr = await api.get("/listino/pending"); setPending(pr.data); } catch { /* noop */ } }
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setImporting(false);
      toast.error(err.response?.data?.detail || "Impossibile avviare l'import");
    }
  };

  const enrichArticoli = async () => {
    setEnriching(true);
    try {
      await api.post("/listino/enrich-articoli");
      const iv = setInterval(async () => {
        try {
          const r = await api.get("/listino/enrich-articoli/status");
          if (r.data.finished) {
            clearInterval(iv); setEnriching(false);
            if (r.data.error) toast.error("Arricchimento fallito: " + r.data.error);
            else { toast.success(r.data.message || "Schede aggiornate"); load(); }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) { setEnriching(false); toast.error(err.response?.data?.detail || "Impossibile avviare"); }
  };

  const aggiornaImmagini = async () => {
    setCachingImg(true);
    try {
      await api.post("/listino/cache-immagini");
      toast.info("Aggiornamento immagini avviato…");
      const iv = setInterval(async () => {
        try {
          const r = await api.get("/listino/cache-immagini/status");
          if (r.data.finished) {
            clearInterval(iv); setCachingImg(false);
            if (r.data.error) toast.error("Aggiornamento immagini fallito: " + r.data.error);
            else { toast.success(r.data.message || "Immagini aggiornate"); load(); }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setCachingImg(false);
      if (err.response?.status === 409) { toast.info("Aggiornamento immagini già in corso…"); }
      else toast.error(err.response?.data?.detail || "Impossibile avviare l'aggiornamento immagini");
    }
  };

  const confirmNew = async () => {
    const codici = Array.from(selCodici);
    if (codici.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    try {
      const all = pending && codici.length === pending.length;
      const r = await api.post("/listino/confirm-new", all ? { all: true } : { codici });
      toast.success(`${r.data.inserted} prodotti inseriti`); setPending(null); load();
    } catch { toast.error("Errore inserimento"); }
  };

  const toggleEnabled = async (p) => {
    try {
      await api.put(`/listino/${p.id}`, { abilitato: !p.abilitato });
      setData((d) => ({ ...d, products: d.products.map((x) => (x.id === p.id ? { ...x, abilitato: !p.abilitato } : x)) }));
    } catch { toast.error("Errore aggiornamento"); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = { ...edit, kwp: Number(edit.kwp) || 0, prezzo: Number(edit.prezzo) || 0, prezzo_target: Number(edit.prezzo_target) || 0, costo: Number(edit.costo) || 0, larghezza_m: Number(edit.larghezza_m) || 0, altezza_m: Number(edit.altezza_m) || 0, lunghezza_m: Number(edit.lunghezza_m) || 0 };
      if (edit.id) await api.put(`/listino/${edit.id}`, body);
      else await api.post("/listino", body);
      toast.success("Salvato"); setEdit(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.codice}" dal listino?`)) return;
    try { await api.delete(`/listino/${p.id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const moveProduct = async (p, direction) => {
    try { await api.post(`/listino/${p.id}/move`, { direction }); load(); }
    catch { toast.error("Errore nel riordino"); }
  };

  const prods = data.products || [];
  const grouped = useMemo(() => {
    const m = {};
    prods.forEach((p) => { const t = p.tipologia || "Senza categoria"; (m[t] = m[t] || []).push(p); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => sortValue(a) - sortValue(b)));
    return Object.entries(m).sort((a, b) => catRank(a[0]) - catRank(b[0]) || a[0].localeCompare(b[0]));
  }, [prods]);

  const pdfGrouped = useMemo(() => grouped
    .map(([cat, ps]) => [cat, ps.filter((p) => p.abilitato)])
    .filter(([, ps]) => ps.length > 0), [grouped]);
  const allCodes = pdfGrouped.flatMap(([, ps]) => ps.map((p) => p.codice));
  const catCodesOf = (cat) => (pdfGrouped.find(([c]) => c === cat)?.[1] || []).map((p) => p.codice);
  const toggleCode = (code) => setSelCodes((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));
  const toggleCatCodes = (cat) => {
    const codes = catCodesOf(cat);
    const allOn = codes.every((c) => selCodes.includes(c));
    setSelCodes((s) => (allOn ? s.filter((c) => !codes.includes(c)) : Array.from(new Set([...s, ...codes]))));
  };
  const catCheckState = (cat) => {
    const codes = catCodesOf(cat);
    const n = codes.filter((c) => selCodes.includes(c)).length;
    return n === 0 ? false : n === codes.length ? true : "indeterminate";
  };
  const toggleExpCat = (cat) => setExpCats((s) => (s.includes(cat) ? s.filter((x) => x !== cat) : [...s, cat]));
  const openPdfCats = () => { setSelCodes(allCodes); setExpCats([]); setPdfCatOpen(true); };
  const openDistCats = () => { setSelCodes(allCodes); setExpCats([]); setLangOpen(true); };
  const marginGroups = useMemo(() => {
    const rows = marginData?.products || [];
    const m = {};
    rows.forEach((r) => { const t = r.tipologia || "Senza categoria"; (m[t] = m[t] || []).push(r); });
    return Object.entries(m).sort((a, b) => catRank(a[0]) - catRank(b[0]) || a[0].localeCompare(b[0]));
  }, [marginData]);

  return (
    <div className="space-y-6" data-testid="listino-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black"><Boxes className="h-7 w-7 text-primary" /> Listino Prodotti</h1>
          <p className="text-sm text-muted-foreground">Catalogo autonomo per il preventivatore. Abilita i prodotti da rendere disponibili.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openPdfCats} disabled={downloadingPdf} data-testid="listino-pdf-btn">{downloadingPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Scarica PDF listino</Button>
          {canCost && (
            <Button variant="outline" size="sm" onClick={openDistCats} disabled={downloadingDist} data-testid="listino-dist-pdf-btn">{downloadingDist ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Globe className="mr-1.5 h-4 w-4" />} Scarica listino distributore</Button>
          )}
          {can && (
            <>
              <Button size="sm" onClick={() => { setImportOpen(true); setImportMsg(""); }} data-testid="listino-import-btn"><DownloadCloud className="mr-1.5 h-4 w-4" /> Importa da gestionale</Button>
              <Button variant="outline" size="sm" onClick={enrichArticoli} disabled={enriching} data-testid="listino-enrich-btn">{enriching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Arricchisci schede</Button>
              <Button variant="outline" size="sm" onClick={aggiornaImmagini} disabled={cachingImg} data-testid="listino-cache-img-btn">{cachingImg ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImageDown className="mr-1.5 h-4 w-4" />} Aggiorna immagini</Button>
            </>
          )}
        </div>
      </div>

      {can && (
        <Card className="border-l-4 border-l-primary p-4" data-testid="listino-note">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="space-y-1.5 text-sm">
              <p className="font-semibold">Come si popola il listino</p>
              <p className="text-muted-foreground"><b className="text-foreground">Importa da gestionale</b>: scarica il listino prezzi dal gestionale Fier. Aggiorna i dati dei prodotti già presenti e propone i nuovi prodotti da confermare (puoi scegliere quali inserire, anche per tipologia). Attivando l'opzione dedicata aggiorna anche i prezzi di vendita.</p>
              <p className="text-muted-foreground"><b className="text-foreground">Arricchisci schede</b>: recupera dal gestionale le schede tecniche dei prodotti già in listino (specifiche e marca) e le aggiorna, senza toccare prezzi né abilitazioni.</p>
              <p className="text-muted-foreground"><b className="text-foreground">Aggiorna immagini</b>: scarica dal gestionale le foto dei prodotti (per i moduli usa quelle specifiche FIER) e le salva sul nostro dominio, così il configuratore le mostra sempre.</p>
              <p className="text-muted-foreground">I prodotti si inseriscono <b className="text-foreground">solo</b> tramite import dal gestionale: non è più possibile aggiungerli manualmente.</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2" data-testid="listino-filters">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Cerca</Label>
            <Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Codice o descrizione…" data-testid="listino-search" />
          </div>
          <div>
            <Label className="text-xs">Tipologia</Label>
            <Select value={f.tipologia} onValueChange={(v) => setF({ ...f, tipologia: v })}>
              <SelectTrigger className="w-[190px]" data-testid="listino-filter-tipologia"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {data.tipologie.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Stato</Label>
            <Select value={f.abilitato} onValueChange={(v) => setF({ ...f, abilitato: v })}>
              <SelectTrigger className="w-[170px]" data-testid="listino-filter-stato"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="on">Solo abilitati</SelectItem>
                <SelectItem value="off">Solo disabilitati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setF({ search: "", tipologia: "all", abilitato: "all" })} data-testid="listino-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Azzera</Button>
        </div>
      </Card>

      {isOwner && (
        <Card className="border-l-4 border-l-emerald-500 p-4" data-testid="listino-margine-slot">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Percent className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <div className="space-y-1 text-sm max-w-2xl">
                <p className="font-semibold">Analisi margini su costo di contabilità</p>
                <p className="text-muted-foreground">A differenza del listino — costruito sul <b className="text-foreground">costo industriale</b> che nasce dal pricing — questa analisi considera il <b className="text-foreground">costo effettivo</b> con cui il bene è registrato in contabilità (dal CSV del magazzino fiscale, ultimo caricato). Mostra il margine % tra quel costo e il prezzo distributore e tra quel costo e il prezzo minimo installatore.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={openMargin} data-testid="listino-margine-btn"><Percent className="mr-1.5 h-4 w-4" /> Vedi analisi margini</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="listino-loading"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></Card>
      ) : prods.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="listino-empty">Nessun prodotto. {can && "Usa \"Importa da gestionale\" per popolare il listino."}</Card>
      ) : grouped.map(([categoria, items]) => (
        <Card key={categoria} className="p-0" data-testid={`listino-cat-${categoria}`}>
          <div className="flex items-center justify-between border-b border-input px-4 py-2.5">
            <h2 className="font-heading text-lg font-black tracking-tight" data-testid={`listino-cat-title-${categoria}`}>{categoria}</h2>
            <Badge variant="secondary" data-testid={`listino-cat-count-${categoria}`}>{items.length} {items.length === 1 ? "prodotto" : "prodotti"}</Badge>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14"></TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">kWp</TableHead>
                  {canCost && <TableHead className="text-right">Costo industriale</TableHead>}
                  {canCost && <TableHead className="text-right whitespace-nowrap text-primary">Prezzo al distributore</TableHead>}
                  <TableHead className="text-right whitespace-nowrap">Prezzo min installatore</TableHead>
                  <TableHead className="text-right">Prezzo di listino</TableHead>
                  <TableHead className="text-right">Sconto</TableHead>
                  <TableHead className="text-right">Prezzo target</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Agg. prezzo</TableHead>
                  <TableHead className="text-center">Abilitato</TableHead>
                  {can && <TableHead className="text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p, i) => (
                  <TableRow key={p.id} data-testid={`listino-row-${p.id}`}>
                    <TableCell className="w-14">{p.immagine_url ? <img src={p.immagine_url} alt="" loading="lazy" className="h-10 w-10 rounded border border-input bg-white object-contain" data-testid={`listino-img-${p.id}`} /> : <div className="h-10 w-10 rounded border border-dashed border-input" />}</TableCell>
                    <TableCell className="font-mono text-xs font-semibold"><span className="inline-flex items-center gap-1">{p.codice}{canMag && <button type="button" onClick={() => setMagDetail(p.codice)} data-testid={`listino-mag-info-${p.id}`} title="Dettaglio disponibilità a magazzino" className="rounded p-0.5 text-primary transition-colors hover:bg-primary/10"><Info className="h-3.5 w-3.5" /></button>}</span></TableCell>
                    <TableCell className="max-w-[360px] text-sm">
                      <button type="button" onClick={() => toggleExpand(p.id)} className="flex w-full items-start gap-1.5 text-left hover:text-primary" data-testid={`listino-desc-toggle-${p.id}`} title={expanded.has(p.id) ? "Riduci" : "Mostra tutto"}>
                        <ChevronRight className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${expanded.has(p.id) ? "rotate-90" : ""}`} />
                        <span className={expanded.has(p.id) ? "" : "line-clamp-2"}>{p.descrizione}</span>
                      </button>
                    </TableCell>
                    <TableCell className="text-right">{kwpN(p.kwp)}</TableCell>
                    {canCost && <TableCell className="text-right text-muted-foreground" data-testid={`listino-costo-${p.id}`}>{eurWp(p.costo, p)}</TableCell>}
                    {canCost && <TableCell className="text-right font-semibold text-primary" data-testid={`listino-prezzo-distributore-${p.id}`}>{eurWp(p.prezzo_distributore, p)}</TableCell>}
                    <TableCell className="text-right font-semibold text-amber-600 dark:text-amber-400" data-testid={`listino-prezzo-min-${p.id}`}>{eurWp(p.prezzo_min_installatore, p)}</TableCell>
                    <TableCell className="text-right font-semibold" data-testid={`listino-prezzo-pubblico-${p.id}`}>{eurWp(p.prezzo, p)}</TableCell>
                    <TableCell className="text-right text-xs font-mono" data-testid={`listino-sconto-${p.id}`}>{p.prezzo > 0 && p.prezzo_target > 0 && p.prezzo_target < p.prezzo ? `-${(Math.round((1 - p.prezzo_target / p.prezzo) * 1000) / 10).toLocaleString("it-IT")}%` : "—"}</TableCell>
                    <TableCell className="text-right font-semibold" data-testid={`listino-prezzo-installatore-${p.id}`}>{eurWp(p.prezzo_target, p)}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap" data-testid={`listino-data-${p.id}`}>{dateN(p.data_listino)}</TableCell>
                    <TableCell className="text-center">
                      {can ? <Switch checked={!!p.abilitato} onCheckedChange={() => toggleEnabled(p)} data-testid={`listino-toggle-${p.id}`} />
                        : <Badge variant={p.abilitato ? "default" : "outline"}>{p.abilitato ? "Sì" : "No"}</Badge>}
                    </TableCell>
                    {can && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0} onClick={() => moveProduct(p, "up")} data-testid={`listino-up-${p.id}`} title="Sposta su"><ArrowUp className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === items.length - 1} onClick={() => moveProduct(p, "down")} data-testid={`listino-down-${p.id}`} title="Sposta giù"><ArrowDown className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit({ ...EMPTY, ...p })} data-testid={`listino-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(p)} data-testid={`listino-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ))}

      {/* Nuovi prodotti da confermare */}
      <Dialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="listino-pending-modal">
          <DialogHeader><DialogTitle>Nuovi prodotti trovati ({pending?.length || 0})</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Questi prodotti sono presenti nel pricing del gestionale ma non ancora nel CRM. Seleziona quelli da inserire nel listino.</p>
          <div className="flex items-center justify-between px-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium" data-testid="listino-pending-select-all">
              <Checkbox checked={allSelected} onCheckedChange={toggleSelAll} data-testid="listino-pending-select-all-cb" />
              {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
            </label>
            <span className="text-xs text-muted-foreground" data-testid="listino-pending-count">{selCodici.size} selezionati</span>
          </div>
          <div className="max-h-80 overflow-y-auto rounded border border-input">
            {pendingGroups.map(([tipologia, prods]) => {
              const codes = prods.map((p) => p.codice);
              const selCount = codes.filter((c) => selCodici.has(c)).length;
              const groupAll = selCount === codes.length;
              return (
                <div key={tipologia} data-testid={`listino-pending-group-${tipologia}`}>
                  <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-2 border-b border-input bg-muted px-3 py-1.5 text-xs font-semibold hover:bg-muted/80" data-testid={`listino-pending-group-head-${tipologia}`}>
                    <Checkbox checked={groupAll} onCheckedChange={(v) => toggleSelGroup(codes, !!v)} data-testid={`listino-pending-group-cb-${tipologia}`} />
                    <span className="flex-1 uppercase tracking-wide">{tipologia}</span>
                    <span className="font-normal text-muted-foreground">{selCount}/{codes.length}</span>
                  </label>
                  {prods.map((p) => (
                    <label key={p.codice} className="flex cursor-pointer items-center gap-2 border-b border-input px-3 py-1.5 pl-6 text-sm hover:bg-accent/40" data-testid={`listino-pending-row-${p.codice}`}>
                      <Checkbox checked={selCodici.has(p.codice)} onCheckedChange={() => toggleSel(p.codice)} data-testid={`listino-pending-cb-${p.codice}`} />
                      <span className="min-w-0 flex-1 truncate"><b className="font-mono text-xs">{p.codice}</b> — {p.descrizione}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Ignora</Button>
            <Button onClick={confirmNew} disabled={selCodici.size === 0} data-testid="listino-confirm-new">Inserisci selezionati ({selCodici.size})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => !importing && setImportOpen(o)}>
        <DialogContent data-testid="listino-import-modal">
          <DialogHeader><DialogTitle>Importa listino dal gestionale</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Vengono importati codice, descrizione, tipologia, kWp, prezzo di listino e ultimo costo. I prodotti nuovi arrivano <b>disabilitati</b>: abilitali tu quelli da usare nel preventivatore.</p>
            <label className="flex items-center gap-2">
              <Checkbox checked={refreshPrices} onCheckedChange={(v) => setRefreshPrices(!!v)} data-testid="listino-refresh-prices" />
              <span>Aggiorna anche prezzi e costi dei prodotti già presenti (sovrascrive le tue modifiche)</span>
            </label>
            {importing && <div className="flex items-center gap-2 text-primary"><Loader2 className="h-4 w-4 animate-spin" /> {importMsg}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>Annulla</Button>
            <Button onClick={runImport} disabled={importing} data-testid="listino-import-confirm">{importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-2 h-4 w-4" />} Avvia import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / add dialog */}
      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="listino-edit-modal">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1"><Label className="text-xs">Codice</Label><Input value={edit.codice} disabled={!!edit.id} onChange={(e) => setEdit({ ...edit, codice: e.target.value })} data-testid="listino-field-codice" /></div>
              <div className="col-span-1"><Label className="text-xs">Tipologia</Label><Input value={edit.tipologia} onChange={(e) => setEdit({ ...edit, tipologia: e.target.value })} data-testid="listino-field-tipologia" /></div>
              <div className="col-span-2"><Label className="text-xs">Descrizione</Label><Input value={edit.descrizione} onChange={(e) => setEdit({ ...edit, descrizione: e.target.value })} data-testid="listino-field-descrizione" /></div>
              <div><Label className="text-xs">Marca</Label><Input value={edit.marca} onChange={(e) => setEdit({ ...edit, marca: e.target.value })} /></div>
              <div><Label className="text-xs">Unità (UM)</Label><Input value={edit.um} onChange={(e) => setEdit({ ...edit, um: e.target.value })} /></div>
              <div><Label className="text-xs">kWp (unitario)</Label><Input type="number" step="0.001" value={edit.kwp} onChange={(e) => setEdit({ ...edit, kwp: e.target.value })} /></div>
              <div><Label className="text-xs">Costo industriale (€)</Label><Input type="number" step="0.01" value={edit.costo} onChange={(e) => setEdit({ ...edit, costo: e.target.value })} data-testid="listino-field-costo" /></div>
              <div><Label className="text-xs">Prezzo target (€)</Label><Input type="number" step="0.01" value={edit.prezzo_target} onChange={(e) => setEdit({ ...edit, prezzo_target: e.target.value })} data-testid="listino-field-prezzo-target" /></div>
              <div><Label className="text-xs">Prezzo all'installatore (€)</Label><Input type="number" step="0.01" value={edit.prezzo} onChange={(e) => setEdit({ ...edit, prezzo: e.target.value })} data-testid="listino-field-prezzo" /></div>
              {(edit.tipologia || "").toLowerCase().includes("modul") && (
                <>
                  <div><Label className="text-xs">Larghezza modulo (m)</Label><Input type="number" step="0.001" value={edit.larghezza_m} onChange={(e) => setEdit({ ...edit, larghezza_m: e.target.value })} data-testid="listino-field-larghezza" /></div>
                  <div><Label className="text-xs">Altezza modulo (m)</Label><Input type="number" step="0.001" value={edit.altezza_m} onChange={(e) => setEdit({ ...edit, altezza_m: e.target.value })} data-testid="listino-field-altezza" /></div>
                </>
              )}
              {(edit.tipologia || "").toLowerCase().includes("fissagg") && (
                <div><Label className="text-xs">Lunghezza barra (m)</Label><Input type="number" step="0.001" value={edit.lunghezza_m} onChange={(e) => setEdit({ ...edit, lunghezza_m: e.target.value })} data-testid="listino-field-lunghezza" /></div>
              )}
              <label className="col-span-1 flex items-center gap-2 pt-5"><Switch checked={!!edit.abilitato} onCheckedChange={(v) => setEdit({ ...edit, abilitato: v })} /> <span className="text-sm">Abilitato</span></label>
              <label className="col-span-1 flex items-center gap-2 pt-5" title="Articolo secondario: nel Magazzino VESTA compare «Su richiesta» invece di «Esaurito»"><Switch checked={!!edit.su_richiesta} onCheckedChange={(v) => setEdit({ ...edit, su_richiesta: v })} data-testid="listino-field-su-richiesta" /> <span className="text-sm">Su richiesta (articolo secondario)</span></label>
              <p className="col-span-2 -mt-1 text-xs text-muted-foreground" data-testid="listino-su-richiesta-help">Da attivare per gli articoli secondari, legati a un prodotto principale, che non hanno giacenza propria e risulterebbero sempre esauriti. Nel Magazzino di VESTA i partner vedranno «Su richiesta» al posto di «Esaurito», senza quantità né date di arrivo. Cambia solo come l'articolo viene mostrato in VESTA: prezzi, listino, preventivi, ordini e magazzino FIER restano invariati.</p>
              {edit.specifiche && Object.keys(edit.specifiche).length > 0 && (
                <div className="col-span-2 rounded border border-input p-2" data-testid="listino-specs">
                  <span className="text-xs font-semibold">Specifiche (da gestionale)</span>
                  <div className="mt-1 flex flex-wrap gap-1">{Object.entries(edit.specifiche).map(([k, v]) => <Badge key={k} variant="secondary" className="text-[10px]">{k}: {v}</Badge>)}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={saving || !edit?.codice} data-testid="listino-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MagazzinoDetailDialog open={!!magDetail} onOpenChange={(o) => !o && setMagDetail(null)} codice={magDetail} quantita={0} dataRichiesta={null} />

      {/* Selezione categorie/prodotti PDF listino prodotti */}
      <Dialog open={pdfCatOpen} onOpenChange={setPdfCatOpen}>
        <DialogContent className="max-w-sm" data-testid="listino-pdf-cat-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileDown className="h-5 w-5 text-primary" /> Cosa stampare</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Destinatario</Label>
                <Select value={pdfDest} onValueChange={setPdfDest}>
                  <SelectTrigger className="mt-1 h-9" data-testid="pdf-dest-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="installatore" data-testid="pdf-dest-installatore">Installatore</SelectItem>
                    <SelectItem value="agenzia" data-testid="pdf-dest-agenzia">Agenzia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lingua</Label>
                <Select value={pdfLang} onValueChange={setPdfLang}>
                  <SelectTrigger className="mt-1 h-9" data-testid="pdf-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it" data-testid="pdf-lang-it">🇮🇹 Italiano</SelectItem>
                    <SelectItem value="en" data-testid="pdf-lang-en">🇬🇧 English</SelectItem>
                    <SelectItem value="de" data-testid="pdf-lang-de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="fr" data-testid="pdf-lang-fr">🇫🇷 Français</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {pdfDest === "installatore" ? "Mostra Prezzo di listino e Prezzo all'installatore (= prezzo target)." : "Mostra Prezzo minimo, Prezzo di listino e Prezzo target."}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Prodotti ({selCodes.length}/{allCodes.length})</span>
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => setSelCodes(selCodes.length === allCodes.length ? [] : allCodes)} data-testid="pdf-cat-toggle-all">{selCodes.length === allCodes.length ? "Deseleziona tutto" : "Seleziona tutto"}</button>
            </div>
            <div className="max-h-72 overflow-auto space-y-1 pr-1">
              {pdfGrouped.map(([cat, items]) => (
                <div key={cat}>
                  <div className="flex items-center gap-2 rounded-md border border-input px-2 py-1.5">
                    <Checkbox checked={catCheckState(cat)} onCheckedChange={() => toggleCatCodes(cat)} data-testid={`pdf-cat-${cat}`} />
                    <button type="button" className="flex-1 flex items-center justify-between text-sm" onClick={() => toggleExpCat(cat)}>
                      <span className="font-medium">{cat}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">{items.filter((p) => selCodes.includes(p.codice)).length}/{items.length}<ChevronRight className={`h-3.5 w-3.5 transition-transform ${expCats.includes(cat) ? "rotate-90" : ""}`} /></span>
                    </button>
                  </div>
                  {expCats.includes(cat) && (
                    <div className="ml-5 mt-1 space-y-0.5 border-l border-input pl-2">
                      {items.map((p) => (
                        <label key={p.codice} className="flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer hover:bg-muted/50" data-testid={`pdf-prod-${p.codice}`}>
                          <Checkbox checked={selCodes.includes(p.codice)} onCheckedChange={() => toggleCode(p.codice)} />
                          <span className="font-mono">{p.codice}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => downloadPdf(selCodes)} disabled={!selCodes.length || downloadingPdf} data-testid="listino-pdf-cat-confirm">{downloadingPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Scarica PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selezione categorie/prodotti + lingua PDF distributore */}
      <Dialog open={langOpen} onOpenChange={setLangOpen}>
        <DialogContent className="max-w-sm" data-testid="listino-dist-lang-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Listino distributore</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Prodotti ({selCodes.length}/{allCodes.length})</span>
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => setSelCodes(selCodes.length === allCodes.length ? [] : allCodes)} data-testid="dist-cat-toggle-all">{selCodes.length === allCodes.length ? "Deseleziona tutto" : "Seleziona tutto"}</button>
            </div>
            <div className="max-h-56 overflow-auto space-y-1 pr-1">
              {pdfGrouped.map(([cat, items]) => (
                <div key={cat}>
                  <div className="flex items-center gap-2 rounded-md border border-input px-2 py-1.5">
                    <Checkbox checked={catCheckState(cat)} onCheckedChange={() => toggleCatCodes(cat)} data-testid={`dist-cat-${cat}`} />
                    <button type="button" className="flex-1 flex items-center justify-between text-sm" onClick={() => toggleExpCat(cat)}>
                      <span className="font-medium">{cat}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">{items.filter((p) => selCodes.includes(p.codice)).length}/{items.length}<ChevronRight className={`h-3.5 w-3.5 transition-transform ${expCats.includes(cat) ? "rotate-90" : ""}`} /></span>
                    </button>
                  </div>
                  {expCats.includes(cat) && (
                    <div className="ml-5 mt-1 space-y-0.5 border-l border-input pl-2">
                      {items.map((p) => (
                        <label key={p.codice} className="flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer hover:bg-muted/50" data-testid={`dist-prod-${p.codice}`}>
                          <Checkbox checked={selCodes.includes(p.codice)} onCheckedChange={() => toggleCode(p.codice)} />
                          <span className="font-mono">{p.codice}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Scegli la lingua del PDF. Intestazioni e descrizioni prodotto verranno tradotte.</p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            {[["it", "🇮🇹 Italiano"], ["en", "🇬🇧 English"], ["de", "🇩🇪 Deutsch"], ["fr", "🇫🇷 Français"]].map(([code, label]) => (
              <Button key={code} variant="outline" onClick={() => downloadDistPdf(code, selCodes)} disabled={!selCodes.length} data-testid={`listino-dist-lang-${code}`}>{label}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Analisi margini su costo di contabilità (CSV magazzino fiscale) */}
      <Dialog open={marginOpen} onOpenChange={setMarginOpen}>
        <DialogContent className="max-w-3xl" data-testid="listino-margine-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 pr-8"><Percent className="h-5 w-5 text-emerald-500" /> Margini su costo di contabilità
            <Button variant="outline" size="sm" className="ml-auto mr-2" onClick={downloadMarginPdf} disabled={marginLoading || downloadingMarginPdf || !marginData} data-testid="listino-margine-pdf-btn">{downloadingMarginPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Scarica PDF</Button>
          </DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Margine % calcolato sul <b className="text-foreground">costo effettivo di contabilità</b> (CSV magazzino fiscale{marginData?.csv_data ? ` al ${dateN(marginData.csv_data)}` : ""}) rispetto al prezzo distributore e al prezzo minimo installatore. Dove manca il costo a CSV il margine non è calcolabile (“—”).
            {marginData ? <span className="ml-1">{marginData.n_con_costo}/{marginData.products.length} prodotti con costo a CSV.</span> : null}
          </p>
          {marginLoading ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="listino-margine-loading"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded border border-input">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>Modello</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Costo CSV</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Marg. distributore</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Marg. min. installatore</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {marginGroups.map(([cat, items]) => (
                    <>
                      <TableRow key={`h-${cat}`} className="bg-muted/60 hover:bg-muted/60">
                        <TableCell colSpan={4} className="py-1.5 text-xs font-bold uppercase tracking-wide" data-testid={`listino-margine-cat-${cat}`}>{cat}</TableCell>
                      </TableRow>
                      {items.map((r) => (
                        <TableRow key={r.codice} data-testid={`listino-margine-row-${r.codice}`}>
                          <TableCell className="font-mono text-xs">{r.codice}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{eurN(r.costo_csv)}</TableCell>
                          <TableCell className="text-right font-semibold">{pctCell(r.margine_dist_pct)}</TableCell>
                          <TableCell className="text-right font-semibold">{pctCell(r.margine_min_pct)}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
